// The leaf types module rather than `@data`, whose barrel pulls the query layer in behind it.
import { ProductEntity, ProductStatus } from '@data/utils/types';

// Nothing ticked means every status, so the list opens on all of them and this ordering is what keeps it
// readable: the products being worked on first, the ones nobody touches last, alphabetical inside each group.
const STATUS_RANK: Record<ProductStatus, number> = {
  [ProductStatus.active]: 0,
  [ProductStatus.support]: 1,
  [ProductStatus.archived]: 2,
  [ProductStatus.deactivated]: 3,
};

/**
 * The rows the Manage list shows: the products the picker allows, grouped by status and alphabetical
 * within each group. Status is filtered server-side, the product picker here, so that the picker's own
 * options keep listing everything the status filter allows rather than only what is already ticked.
 */
export type ProductPickerOption = { value: string; label: string };

/**
 * Options for the product picker: everything the status filter currently allows, plus anything already
 * ticked. `knownNames` is the caller's cache and is written to here — a product the status filter has
 * since excluded has left the fetch, and without its remembered name the picker would show a blank chip
 * that cannot be identified or unticked by name.
 */
export const productPickerOptions = (
  products: ProductEntity[],
  selectedProductIds: string[],
  knownNames: Map<string, string>,
  labelOf: (product: ProductEntity) => string,
): ProductPickerOption[] => {
  products.forEach(product => knownNames.set(product.id, labelOf(product)));
  const ids = new Set([...products.map(p => p.id), ...selectedProductIds]);
  return [...ids]
    .map(id => ({ value: id, label: knownNames.get(id) ?? '' }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * What a reader means by "the client": the company the work is done for. The client person behind it is
 * the older, narrower link and stands in when no company is set, so a product from before epic 12 still
 * says who it belongs to. Same order of preference as the product's own detail card, which is the point
 * — the list and the page were showing different clients for one product.
 */
export const clientLabelOf = (product: { clientCompanyName?: string; clientName?: string }): string =>
  product.clientCompanyName || product.clientName || '';

// The picker value for products that belong to no client, such as Holiday or Bizdev.
export const NO_CLIENT = '__no-client__';

export const matchesClientFilter = (
  product: { clientCompanyName?: string; clientName?: string },
  clientNames: string[],
): boolean => !clientNames.length || clientNames.includes(clientLabelOf(product) || NO_CLIENT);

/** The client picker's options: every client named on these products, then "No client" if any has none. */
export const clientPickerOptions = (
  products: { clientCompanyName?: string; clientName?: string }[],
  selected: string[],
  noClientLabel: string,
): ProductPickerOption[] => {
  const labels = products.map(clientLabelOf);
  const names = [...new Set([...labels, ...selected])].filter(name => name && name !== NO_CLIENT);
  const options = names.sort((a, b) => a.localeCompare(b)).map(name => ({ value: name, label: name }));
  return labels.includes('') || selected.includes(NO_CLIENT)
    ? [...options, { value: NO_CLIENT, label: noClientLabel }]
    : options;
};

export const narrowAndOrderProducts = (
  products: ProductEntity[],
  selectedProductIds: string[],
  clientNames: string[] = [],
): ProductEntity[] =>
  products
    .filter(product => !selectedProductIds.length || selectedProductIds.includes(product.id))
    .filter(product => matchesClientFilter(product, clientNames))
    .sort((a, b) => {
      const rank = (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99);
      return rank !== 0 ? rank : (a.name ?? '').localeCompare(b.name ?? '');
    });
